/** @jsxRuntime classic */
import React, { useState } from 'react';
import {
  CardContent,
  Typography,
  Grid,
  Box,
  makeStyles,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';

// ✅ Correct relative imports from home/image/
import jiraLogo from './image/jira.png';
import grafanaLogo from './image/Grafana.png';
import confluenceLogo from './image/Confluence.png';
import azureLogo from './image/azure.png';

const useStyles = makeStyles(theme => ({
  linksCard: {
    height: '100%',
  },
  linkWrap: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    outline: 'none',
  },
  circle: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    margin: '0 auto',
    border: `2px solid ${theme.palette.grey[500]}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      theme.palette.type === 'dark' ? '#2f2f2f' : '#eeeeee',
    overflow: 'hidden',
    transition: 'transform 120ms ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.05)', // subtle hover effect
    },
  },
  logo: {
    width: 60,
    height: 60,
    objectFit: 'contain',
    display: 'block',
  },
  label: {
    textAlign: 'center',
    marginTop: theme.spacing(1),
    fontWeight: 600,
    fontSize: '0.875rem',
  },
}));

type LinkItem = {
  label: string;
  href?: string;
  imgSrc: string;
  aria?: string;
  subLinks?: { name: string; url: string }[];
};

const LINKS: LinkItem[] = [
  {
    label: 'Jira',
    href: 'https://sitecore.atlassian.net/jira/projects?page=1&sortKey=name&sortOrder=ASC&types=software',
    imgSrc: jiraLogo,
    aria: 'Open Jira',
  },
  {
    label: 'Grafana',
    imgSrc: grafanaLogo,
    aria: 'Open Grafana',
    subLinks: [
      { name: 'Prod-WUE', url: 'https://grafana-prod-euw.sitecorecloud.app/?orgId=1&from=now-6h&to=now&timezone=browser' },
      { name: 'Prod-USE', url: 'https://grafana-prod-use.sitecorecloud.app/?orgId=1&from=now-6h&to=now&timezone=browser' },
      { name: 'Prod-USW', url: 'https://grafana-prod-usw.sitecorecloud.app/?orgId=1&from=now-6h&to=now&timezone=browser' },
      { name: 'Prod-JPE', url: 'https://grafana-prod-jpe.sitecorecloud.app/?orgId=1&from=now-6h&to=now&timezone=browser' },
      { name: 'Prod-AUE', url: 'https://grafana-prod-aue.sitecorecloud.app/?orgId=1&from=now-6h&to=now&timezone=browser' },
    ],
  },
  {
    label: 'Confluence',
    href: 'https://sitecore.atlassian.net/wiki/home',
    imgSrc: confluenceLogo,
    aria: 'Open Confluence',
  },
  {
    label: 'Azure',
    href: 'https://portal.azure.com/#home',
    imgSrc: azureLogo,
    aria: 'Open Azure Portal',
  },
];

export const LinksCard = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, label: string) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(label);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  return (
    <InfoCard title="Links" className={classes.linksCard}>
      <CardContent>
        <Grid container spacing={3}>
          {LINKS.map(({ label, href, imgSrc, aria, subLinks }) => (
            <Grid item xs={12} sm={3} key={label}>
              {subLinks ? (
                <>
                  <Box
                    className={classes.circle}
                    onClick={(e) => handleMenuOpen(e, label)}
                    aria-label={aria || label}
                  >
                    <img
                      className={classes.logo}
                      src={imgSrc}
                      alt={`${label} logo`}
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        el.style.display = 'none';
                        el.parentElement!.textContent = label[0];
                      }}
                    />
                  </Box>
                  <Typography className={classes.label}>{label}</Typography>
                  <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={activeMenu === label}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    getContentAnchorEl={null}
                    style={{ marginTop: 10 }} // extra space below icon
                  >
                    {subLinks.map((link) => (
                      <MenuItem
                        key={link.name}
                        onClick={() => {
                          window.open(link.url, '_blank');
                          handleMenuClose();
                        }}
                      >
                        {link.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : (
                <a
                  className={classes.linkWrap}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={aria || label}
                >
                  <Box className={classes.circle}>
                    <img
                      className={classes.logo}
                      src={imgSrc}
                      alt={`${label} logo`}
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        el.style.display = 'none';
                        el.parentElement!.textContent = label[0];
                      }}
                    />
                  </Box>
                  <Typography className={classes.label}>{label}</Typography>
                </a>
              )}
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </InfoCard>
  );
};